import { useEffect, useRef, useState } from "react";
import { SignOutButton } from "@clerk/react";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { Logo } from "@/pages/AuthLayout";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export function OtpGate() {
  const { reload } = useMe();
  const [code, setCode] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const asked = useRef(false);

  const send = async () => {
    setError("");
    try {
      const { data } = await api.post("/auth/otp/send");
      if (data.already_verified) return reload();
      setSentTo(data.sent_to);
      toast.success("Code envoyé par email");
    } catch (e) { setError(errMsg(e)); }
  };
  useEffect(() => { if (!asked.current) { asked.current = true; send(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const verify = async (value = code) => {
    if (value.length !== 6) return;
    setBusy(true); setError("");
    try { await api.post("/auth/otp/verify", { code: value }); await reload(); }
    catch (e) { setError(errMsg(e)); setCode(""); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50" data-testid="otp-gate">
      <div className="max-w-md w-full bg-white border rounded-xl p-8 text-center fade-up">
        <div className="flex justify-center mb-6"><Logo /></div>
        <MailCheck className="h-10 w-10 mx-auto text-[#1E3A8A]" />
        <h1 className="font-display text-xl font-bold mt-4">Vérification en deux étapes</h1>
        <p className="text-sm text-slate-600 mt-2">
          {sentTo ? <>Saisissez le code à 6 chiffres envoyé à <strong>{sentTo}</strong>.</> : "Envoi du code de connexion…"}
        </p>
        <div className="flex justify-center mt-6">
          <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} disabled={busy} data-testid="otp-input">
            <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
          </InputOTP>
        </div>
        {error && <p className="text-sm text-red-600 mt-3" data-testid="otp-error">{error}</p>}
        <Button className="w-full mt-6" onClick={() => verify()} disabled={busy || code.length !== 6} data-testid="otp-verify-btn">
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Valider
        </Button>
        <div className="flex justify-between mt-4 text-sm">
          <button onClick={send} className="text-[#1E3A8A] hover:underline" data-testid="otp-resend-btn">Renvoyer le code</button>
          <SignOutButton><button className="text-slate-500 hover:underline" data-testid="otp-signout-btn">Se déconnecter</button></SignOutButton>
        </div>
        <p className="text-xs text-slate-400 mt-6">Un code est demandé à chaque connexion pour protéger les documents de l'entreprise.</p>
      </div>
    </div>
  );
}
